import { ReactElement } from 'react';
import { LayoutType } from '@sandtech-hub-monorepo/ui-kit';

export enum RouteAccess {
  PRIVATE = 'private',
  PUBLIC = 'public',
}

export enum ProfileTabSections {
  Personal = 'personal',
  Achievements = 'achievements',
}

export enum LearningTabSections {
  Current = 'current',
  Past = 'past',
  All = 'all',
  Achievements = 'achievements',
}

export const AppRoutesPaths = {
  Home: '/',
  Learners: '/learners',
  /*
   * Carries an abbreviation, not an id — there is no learner-facing query that
   * fetches a program by id, and the abbreviation is the only readable stable
   * key on Program.
   *
   * The name is wrong and has to stay wrong. `:programId` is the param the
   * shared ui-kit-site components hardcode, both when they build links
   * (`programPageLink.replace(':programId', abbreviation)` in ProgramCard,
   * RelatedProgramCard and ProgramsPreview) and when they read one
   * (`useParams().programId` in ProgramPage). set-edu renders those components
   * for degree learners, and central-hub routes `/program/:programId` into the
   * same ones. Renaming here alone produced links to the literal
   * `/program/:abbreviation` and a detail page that never resolved.
   *
   * Fixing it properly means renaming in both apps and the library together.
   */
  Program: '/program/:programId',
  Learning: '/learning',
  Programs: '/programs',
  Community: '/community',
  // Learner-facing undergraduate admissions home: the institution page and
  // apply flow the degree path leads to from the program-path chooser.
  Undergraduate: '/undergraduate',
  Settings: '/settings',
  Transcript: '/transcript',
  Onboarding: '/onboarding',
  Profile: '/profile',
  Subscribe: '/subscribe',
  SubscribeCheckout: '/subscribe/checkout',
  // Reached from the checkout page's own onComplete handler, carrying
  // ?checkoutSessionId= and ?programId=. Stripe does not navigate here itself:
  // the sessions are minted with redirect_on_completion: 'never'.
  SubscribeConfirmation: '/subscribe/confirmation',
  PaymentFAQS: '', // TODO: Fill this in

  // Public recipient-facing secure link from transcript delivery emails.
  TranscriptVerify: '/transcripts/verify/:verificationCode',
  // Public credential pages; the gateway's /ob3/verify(-certificate) links
  // redirect here, so the paths must match credential.routes.ts.
  CertificateVerify: '/certificates/verify/:verificationCode',
  BadgeVerify: '/badges/verify/:verificationCode',

  //   Auth Routes
  Login: '/login',
  Logout: '/logout',
  SignUp: '/sign-up',
  ConfirmSignUp: '/sign-up-confirmation',
  ResetPassword: '/reset-password',
  CreatePassword: '/create-password',
  OauthToken: '/oauth/token',

  /*
   * A real path, not the catch-all pattern: this is somewhere we navigate TO,
   * and `'*'` as a destination resolves relative to the current route rather
   * than to a fixed url. Nothing routes it explicitly - the catch-all in
   * routes.tsx matches it and renders the 404.
   */
  NotFound: '/not-found',
};

/**
 * Routes one learner path owns outright. Everything not listed is shared and
 * renders per path instead — `/`, `/learning`, `/programs` and
 * `/program/:programId` each choose their view.
 *
 * Keyed by the literal path, which works because none of these take a param.
 *
 * Audited 2026-09-15: no control anywhere in the app leads a learner to a
 * route on the other side. Every link to one of these is rendered on a surface
 * only its own path can reach, the header menu and footer contain none of
 * them, and no shared ui-kit / ui-kit-site component hardcodes one — they take
 * link props, and set-edu passes only the shared routes into them.
 *
 * So the guard is not backing up a leak that exists today. It is there for
 * typed urls, bookmarks, links in old emails, and the next control somebody
 * adds without thinking about paths. Which means you cannot see it working by
 * clicking around: you have to type one of these paths as the wrong learner.
 */
export const ROUTE_OWNER: Record<string, 'gsa' | 'degree'> = {
  [AppRoutesPaths.Subscribe]: 'gsa',
  [AppRoutesPaths.SubscribeCheckout]: 'gsa',
  [AppRoutesPaths.SubscribeConfirmation]: 'gsa',
  [AppRoutesPaths.Undergraduate]: 'degree',
  [AppRoutesPaths.Transcript]: 'degree',
};

/**
 * The plans page for a program. The id is not decoration: the plan list is
 * scoped to one program, and checkout refuses a plan that does not cover it.
 * Omit it only where there is genuinely no program in hand.
 */
export const subscribePath = (programId?: string | null): string =>
  programId
    ? `${AppRoutesPaths.Subscribe}?${new URLSearchParams({ programId })}`
    : AppRoutesPaths.Subscribe;

export interface IRouteDefinition {
  path: string;
  name?: string;
  element: ReactElement;
  layout: LayoutType;
}
